import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import {
  PhoneArrowUpRightIcon,
  PhoneArrowDownLeftIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { cdrAPI } from '../../services/api';
import toast from 'react-hot-toast';

const CallHistoryPage = () => {
  const [calls, setCalls] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filters, setFilters] = useState({
    start_date: format(new Date().setDate(new Date().getDate() - 7), 'yyyy-MM-dd'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    extension: '',
  });
  const [pagination, setPagination] = useState({ limit: 50, offset: 0, total: 0 });

  useEffect(() => {
    fetchCalls();
  }, [filters, pagination.offset]);

  const fetchCalls = async () => {
    setIsLoading(true);
    try {
      const response = await cdrAPI.getAll({
        ...filters,
        limit: pagination.limit,
        offset: pagination.offset,
      });
      const data = response.data;
      setCalls(data?.records || data?.data || data || []);
      setPagination(prev => ({ ...prev, total: data?.total || 0 }));
    } catch (error) {
      toast.error('Failed to load call history');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getDispositionBadge = (disposition) => {
    const badges = {
      ANSWERED: 'badge-success',
      'NO ANSWER': 'badge-warning',
      BUSY: 'badge-warning',
      FAILED: 'badge-danger',
      CONGESTION: 'badge-danger',
    };
    return badges[disposition] || 'badge-info';
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div>
        <h1 className="text-2xl font-bold text-white">Call History</h1>
        <p className="text-slate-400 mt-1">View and search call detail records</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center gap-2 mb-4">
          <FunnelIcon className="w-5 h-5 text-slate-400" />
          <span className="font-medium text-white">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="label">Start Date</label>
            <input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">End Date</label>
            <input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              className="input"
            />
          </div>
          <div>
            <label className="label">Extension</label>
            <input
              type="text"
              value={filters.extension}
              onChange={(e) => setFilters({ ...filters, extension: e.target.value })}
              className="input"
              placeholder="Any extension"
            />
          </div>
          <div className="flex items-end">
            <button onClick={fetchCalls} className="btn-primary w-full">
              <MagnifyingGlassIcon className="w-4 h-4 mr-2" />
              Search
            </button>
          </div>
        </div>
      </div>

      {/* Results */}
      {isLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="spinner" />
        </div>
      ) : calls.length > 0 ? (
        <>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Direction</th>
                  <th>Date/Time</th>
                  <th>From</th>
                  <th>To</th>
                  <th>Duration</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {calls.map((call, index) => {
                  const isInbound = call.direction === 'inbound' || call.dcontext?.includes('incoming');
                  return (
                    <tr key={call.uniqueid || index}>
                      <td>
                        <div className={`p-2 rounded-lg inline-flex ${isInbound ? 'bg-blue-500/20 text-blue-400' : 'bg-green-500/20 text-green-400'}`}>
                          {isInbound ? (
                            <PhoneArrowDownLeftIcon className="w-4 h-4" />
                          ) : (
                            <PhoneArrowUpRightIcon className="w-4 h-4" />
                          )}
                        </div>
                      </td>
                      <td className="text-slate-300">
                        {call.calldate || call.timestamp
                          ? format(new Date(call.calldate || call.timestamp), 'MMM d, yyyy h:mm a')
                          : '-'}
                      </td>
                      <td>
                        <span className="font-mono text-white">{call.src || call.caller_id || '-'}</span>
                        {call.clid && <span className="text-slate-500 text-xs block">{call.clid}</span>}
                      </td>
                      <td>
                        <span className="font-mono text-white">{call.dst || call.destination || '-'}</span>
                      </td>
                      <td className="text-slate-300">{formatDuration(call.duration || call.billsec)}</td>
                      <td>
                        <span className={`badge ${getDispositionBadge(call.disposition || call.status)}`}>
                          {call.disposition || call.status || 'Unknown'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-slate-400">
              Showing {pagination.offset + 1} - {Math.min(pagination.offset + calls.length, pagination.total || calls.length)} 
              {pagination.total > 0 && ` of ${pagination.total}`}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }))}
                disabled={pagination.offset === 0}
                className="btn-secondary disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }))}
                disabled={calls.length < pagination.limit}
                className="btn-secondary disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="card text-center py-12">
          <p className="text-slate-400">No calls found for the selected criteria</p>
        </div>
      )}
    </div>
  );
};

export default CallHistoryPage;
